import { DatePickerView } from '@mui/lab/DatePicker/shared';
import { Checkbox, FormControlLabel, Grid, useEventCallback } from '@mui/material';
import { FormikErrors, FormikTouched } from 'formik';
import { DateTime } from 'luxon';
import { memo, useMemo } from 'react';
import { PmiButton } from 'shell/components/shared/button';
import ChooseDate from 'shell/components/shared/choice-date';
import ListGender from 'shell/components/shared/gender';
import PmiTextField from 'shell/components/shared/text-field';
import PmiTooltipError from 'shell/components/shared/tooltip-error';
import { regexNumericFiscalCode } from 'shell/constants/regex';
import { PersonalDataVM } from 'shell/view-models/personal-data';

interface GeneralityComponentProps {
  onChange: (key: keyof PersonalDataVM, value: PersonalDataVM[keyof PersonalDataVM]) => void;
  values?: PersonalDataVM;
  resetForm?: () => void;
  onSearch: () => void;
  disabled?: boolean;
  onChangeFields: (values: PersonalDataVM) => void;
  personFilterAccountholderBlur?: (fieldName: string) => void;
  errors?: FormikErrors<PersonalDataVM>;
  touched?: FormikTouched<PersonalDataVM>;
}

const GeneralityComponent = ({
  onChange,
  values,
  resetForm,
  onSearch,
  disabled,
  onChangeFields,
  personFilterAccountholderBlur = () => {
    return;
  },
  errors: errorsGenerality,
  touched: touchedGenerality,
}: GeneralityComponentProps): React.ReactElement => {
  const handleBirthDate = useEventCallback((value?: DateTime | null, choice?: DatePickerView[]) => {
    onChangeFields({
      ...(values || {}),
      birthday: value,
      birthdayChoice: choice,
    });
  });

  const fiscalCodeMaxLength = useMemo(() => {
    if (values?.fiscalCode) {
      if (regexNumericFiscalCode.test(values?.fiscalCode) && values?.fiscalCode?.length >= 10) {
        return '11';
      }
      return '16';
    }
  }, [values?.fiscalCode]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <PmiTextField
          label="Codice fiscale"
          aria-label="Codice fiscale"
          name="accountholderData.fiscalCode"
          helperText={errorsGenerality?.fiscalCode}
          error={touchedGenerality?.fiscalCode && Boolean(errorsGenerality?.fiscalCode)}
          id="fiscalCode"
          fullWidth
          onBlur={() => personFilterAccountholderBlur('accountholderData.fiscalCode')}
          value={values?.fiscalCode?.toUpperCase() || ''}
          onChange={(event) => onChange('fiscalCode', event.target.value)}
          disabled={disabled}
          inputProps={{ maxLength: fiscalCodeMaxLength ? fiscalCodeMaxLength : '16' }}
        />
      </Grid>
      <Grid item container spacing={1} xs={12} alignItems="center">
        <Grid item xs={12} md={9}>
          <PmiTextField
            label="Cognome"
            aria-label="Cognome"
            id="surname"
            fullWidth
            value={values?.surname || ''}
            onChange={(event) => onChange('surname', event.target.value)}
            disabled={disabled || values?.withoutSurname}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControlLabel
            control={
              <Checkbox
                id="withoutSurname"
                checked={values?.withoutSurname}
                onChange={() =>
                  onChangeFields({
                    surname: !values?.withoutSurname ? '' : values?.surname || '',
                    withoutSurname: !values?.withoutSurname,
                  })
                }
                disabled={disabled}
                inputProps={{ 'aria-label': 'Persona senza cognome' }}
              />
            }
            label="Persona senza cognome"
          />
        </Grid>
      </Grid>
      <Grid item container spacing={1} xs={12} alignItems="center">
        <Grid item xs={12} md={9}>
          <PmiTextField
            label="Nome"
            aria-label="Nome"
            id="name"
            fullWidth
            value={values?.name || ''}
            onChange={(event) => onChange('name', event.target.value)}
            disabled={disabled || values?.withoutName}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControlLabel
            control={
              <Checkbox
                id="withoutName"
                checked={values?.withoutName}
                onChange={() =>
                  onChangeFields({
                    name: !values?.withoutName ? '' : values?.name || '',
                    withoutName: !values?.withoutName,
                  })
                }
                disabled={disabled}
                inputProps={{ 'aria-label': 'Persona senza nome' }}
              />
            }
            label="Persona senza nome"
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <ChooseDate
          selected={values?.birthday}
          choice={values?.birthdayChoice}
          onChange={handleBirthDate}
          disabled={disabled}
        />
      </Grid>
      <Grid item container spacing={1} alignItems="center" xs={12}>
        <Grid item xs={12} md={6}>
          <ListGender
            value={values?.gender || ''}
            onChange={(event) => onChange('gender', event.target.value)}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <PmiTooltipError
            show={!!errorsGenerality?.fiscalCode}
            message={errorsGenerality?.fiscalCode}
          >
            <span>
              <PmiButton
                variant="contained"
                color="secondary"
                fullWidth
                disabled={
                  (!values?.fiscalCode &&
                    !(
                      (values?.name || values?.withoutName) &&
                      (values?.surname || values?.withoutSurname)
                    )) ||
                  (touchedGenerality?.fiscalCode && Boolean(errorsGenerality?.fiscalCode))
                }
                onClick={onSearch}
              >
                Cerca
              </PmiButton>
            </span>
          </PmiTooltipError>
        </Grid>
        {resetForm ? (
          <Grid item xs={12} md={2}>
            <PmiButton variant="outlined" color="cdmBrand" fullWidth onClick={resetForm}>
              Pulisci
            </PmiButton>
          </Grid>
        ) : null}
      </Grid>
    </Grid>
  );
};

GeneralityComponent.displayName = 'FormFilter component for accountholder generality';

const FormFilterAccountHolderGenerality = memo(GeneralityComponent);

export default FormFilterAccountHolderGenerality;
